import * as React from 'react';
import {
  EventType,
  InteractionStatus,
  InteractionType,
  type AccountInfo,
  type EventMessage
} from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';

import {
  buildGraphTokenRequest,
  buildLoginRequest,
  buildTokenRequest,
  getRedirectUri
} from '@/lib/auth-config';
import { acceptExplicitSignIn, beginExplicitSignIn, failExplicitSignIn, locallySignedOut, restorePanelSignIn, retireLocalSession, SIGN_IN_COMPLETION_PREFIX } from '@/lib/local-session';
import { consumeAuthIntent } from '@/lib/auth-navigation';

function isAccountInfo(value: unknown): value is AccountInfo {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<AccountInfo>;
  return typeof candidate.homeAccountId === 'string' && typeof candidate.username === 'string';
}

function accountFromEventPayload(payload: unknown): AccountInfo | null {
  if (isAccountInfo(payload)) return payload;
  if (payload && typeof payload === 'object' && 'account' in payload) {
    const account = (payload as { account?: unknown }).account;
    return isAccountInfo(account) ? account : null;
  }
  return null;
}

function isMsalStorageKey(key: string | null): boolean {
  return key?.toLowerCase().includes('msal') ?? false;
}

/**
 * Single global owner for:
 * - active-account writes from LOGIN_SUCCESS
 * - top-level `authAction` intents (iframe → tab handoff)
 * - cross-tab MSAL cache pickup (storage / focus / visibility)
 *
 * Must mount once under MsalProvider. Application hooks must not register
 * competing URL-driven login effects.
 */
export function AuthLifecycle(): null {
  const { instance, inProgress } = useMsal();
  const intentConsumed = React.useRef(false);

  // Active account writer — set only from LOGIN_SUCCESS payload (no ACTIVE_ACCOUNT_CHANGED loop).
  React.useEffect(() => {
    const callbackId = instance.addEventCallback((message: EventMessage) => {
      if (message.eventType === EventType.LOGIN_SUCCESS || message.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) {
        const account = accountFromEventPayload(message.payload);
        if (!account) return;
        const recovering = locallySignedOut();
        if (recovering) {
          const payload = message.payload;
          if (message.interactionType !== InteractionType.Redirect || !payload || typeof payload !== 'object' || !('state' in payload) || !('fromCache' in payload) ||
            !acceptExplicitSignIn(payload.state, payload.fromCache, account)) return;
        }
        const active = instance.getActiveAccount();
        // Background results for another account cannot switch the selected identity.
        if (recovering || !active || (active.homeAccountId === account.homeAccountId &&
          active.tenantId === account.tenantId && active.localAccountId === account.localAccountId)) {
          instance.setActiveAccount(account);
        }
        return;
      }

      // An old provider logout callback must not terminate a new verified session.
      if (message.eventType === EventType.LOGOUT_SUCCESS && locallySignedOut()) {
        instance.setActiveAccount(null);
      }
    });

    return () => {
      if (callbackId) instance.removeEventCallback(callbackId);
    };
  }, [instance]);

  // A completion is bound to the panel's explicit attempt. Reinitialize MSAL's
  // cache once before selecting the exact completed identity and rediscovering access.
  React.useEffect(() => {
    if (inProgress !== InteractionStatus.None) return;
    const restore = () => {
      const account = restorePanelSignIn(instance.getAllAccounts(), () => window.location.reload());
      if (account) instance.setActiveAccount(account);
    };
    const onStorage = (event: StorageEvent) => {
      if (event.storageArea === window.localStorage && event.key?.startsWith(SIGN_IN_COMPLETION_PREFIX)) restore();
    };
    const onVisibility = () => { if (document.visibilityState === 'visible') restore(); };
    restore();
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', restore);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', restore);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [instance, inProgress]);

  // Cross-tab pickup — no-op when an active account already exists.
  React.useEffect(() => {
    const pickUpIfNeeded = () => {
      if (locallySignedOut() || instance.getActiveAccount()) return;
      const accounts = instance.getAllAccounts();
      if (accounts[0]) {
        instance.setActiveAccount(accounts[0]);
      }
    };

    const onStorage = (event: StorageEvent) => {
      if (event.storageArea !== window.localStorage || !isMsalStorageKey(event.key)) return;
      pickUpIfNeeded();
    };

    const onFocus = () => pickUpIfNeeded();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') pickUpIfNeeded();
    };

    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [instance]);

  // Consume single-use top-level auth intents (from iframe handoff).
  React.useEffect(() => {
    if (intentConsumed.current) return;
    if (inProgress !== InteractionStatus.None) return;

    const intent = consumeAuthIntent();
    if (!intent) {
      // Mark consumed so we don't re-scan forever; param is already absent.
      intentConsumed.current = true;
      return;
    }
    intentConsumed.current = true;

    const redirectStartPage = intent.returnTo
      ? `${window.location.origin}${intent.returnTo}`
      : window.location.href;

    if (intent.action === 'login') {
      const attempt = beginExplicitSignIn(intent.signInHandoff);
      try {
        void instance.loginRedirect({ ...buildLoginRequest(redirectStartPage), state: attempt, prompt: 'login' })
          .catch(() => { failExplicitSignIn(attempt); console.error('Microsoft sign-in could not start.'); });
      } catch { failExplicitSignIn(attempt); }
      return;
    }

    if (intent.action === 'logout') {
      const account = intent.account ?? instance.getActiveAccount() ?? undefined;
      retireLocalSession(account);
      // Missing targeting must never bulk-clear other accounts.
      if (!account) return;
      try {
        void instance.logoutRedirect({ account, postLogoutRedirectUri: getRedirectUri() })
          .catch(() => { console.error('Microsoft sign-out remains unconfirmed. Retry from this panel.'); });
      } catch { console.error('Microsoft sign-out remains unconfirmed. Retry from this panel.'); }
      return;
    }

    if (locallySignedOut()) return;

    // acquireToken / Graph consent — interaction-required recovery
    const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0] ?? null;
    if (!account) {
      void instance.loginRedirect(buildLoginRequest(redirectStartPage)).catch((error: unknown) => {
        console.error('MSAL loginRedirect failed', error);
      });
      return;
    }

    const tokenRequest =
      intent.action === 'acquireGraphCalendar'
        ? buildGraphTokenRequest(account, redirectStartPage, 'calendar')
        : intent.action === 'acquireGraphPlaces'
          ? buildGraphTokenRequest(account, redirectStartPage, 'places')
          : intent.action === 'acquireGraphPresence'
            ? buildGraphTokenRequest(account, redirectStartPage, 'presence')
            : intent.action === 'acquireGraphManagerReports'
              ? buildGraphTokenRequest(account, redirectStartPage, 'managerReports')
              : intent.action === 'acquireGraphGroups'
                ? buildGraphTokenRequest(account, redirectStartPage, 'groups')
              : intent.action === 'acquireGraphDirectory'
                ? buildGraphTokenRequest(account, redirectStartPage, 'directory')
                : buildTokenRequest(account, redirectStartPage);

    void instance.acquireTokenRedirect(tokenRequest).catch(() => {
      console.error('Microsoft consent could not be completed. Retry from the application.');
    });
  }, [instance, inProgress]);

  return null;
}
